import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Upload,
  QrCode,
  UserCheck,
  ListOrdered,
  Radio,
  Tv2,
  Database,
  BookOpen,
  Building2,
  Tag,
  Layers,
  CalendarDays,
  Smartphone,
  LogOut,
  PartyPopper,
  GraduationCap,
  ClipboardList,
} from "lucide-react";

import { api } from "@/lib/api";
import { clearSession, getSession, isViewer } from "@/lib/auth";
import { LOGOUT } from "@/constants/testIds/auth";

const LOGO = "/mu-logo.png";

const VIEWER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/registrations", label: "Registrations", icon: Users, id: "nav-registrations" },
  { to: "/checkin-transactions", label: "Check-in Transactions", icon: ClipboardList, id: "nav-checkin-transactions" },
];

const SECTIONS = [
  {
    title: "Operations",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
      { to: "/students", label: "Students (Masterlist)", icon: GraduationCap, id: "nav-students" },
      { to: "/registrations", label: "Registrations", icon: Users, id: "nav-registrations" },
      { to: "/upload", label: "Upload Registrations", icon: Upload, id: "nav-upload" },
      { to: "/checkin/student", label: "Student Check-in", icon: QrCode, id: "nav-checkin-student" },
      { to: "/checkin/guest", label: "Guest Check-in", icon: UserCheck, id: "nav-checkin-guest" },
      { to: "/checkin/party", label: "Night Party Check-in", icon: PartyPopper, id: "nav-checkin-party" },
      { to: "/checkin-transactions", label: "Check-in Transactions", icon: ClipboardList, id: "nav-checkin-transactions" },
    ],
  },
  {
    title: "Master Data",
    items: [
      { to: "/master/programs", label: "Student Programs", icon: BookOpen, id: "nav-programs" },
      { to: "/master/entities", label: "Entities", icon: Building2, id: "nav-entities" },
      { to: "/master/specializations", label: "Specializations", icon: Tag, id: "nav-specializations" },
      { to: "/master/categories", label: "Student Categories", icon: Layers, id: "nav-categories" },
      { to: "/sequence", label: "Sequence Configuration", icon: ListOrdered, id: "nav-sequence" },
    ],
  },
  {
    title: "Live",
    items: [
      { to: "/backstage", label: "Backstage", icon: Radio, id: "nav-backstage" },
    ],
  },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const session = getSession();
  const readOnly = isViewer();
  const [activeEvent, setActiveEvent] = useState(null);

  const navSections = readOnly
    ? [{ title: "Reports", items: VIEWER_NAV }]
    : SECTIONS;

  const logout = () => {
    clearSession();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    api.get("/events/active").then((r) => setActiveEvent(r.data?.id ? r.data : null)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex bg-slate-50 text-foreground">
      <aside
        data-testid="app-sidebar"
        className="w-64 shrink-0 bg-white border-r border-border flex flex-col"
      >
        <div className="px-6 py-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
            <img src={LOGO} alt="Mahindra University" className="h-10 w-auto" />
          </Link>
          <p className="mt-4 kicker">Convocation Portal</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto">
          {navSections.map((sec) => (
            <div key={sec.title}>
              <p className="px-3 mb-1 text-[10px] tracking-[0.2em] uppercase font-semibold text-slate-400 flex items-center gap-1.5">
                {sec.title === "Master Data" && <Database size={11} />} {sec.title}
              </p>
              <div className="space-y-0.5">
                {sec.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={n.id}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-primary/10 text-primary font-semibold"
                          : "text-slate-600 hover:bg-slate-100"
                      }`
                    }
                  >
                    <n.icon size={16} className="shrink-0" />
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-border space-y-2">
          {session?.name && (
            <div className="px-3 py-2 text-xs text-slate-500">
              Signed in as <span className="font-medium text-slate-700">{session.name}</span>
              {readOnly && (
                <span className="ml-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                  Read only
                </span>
              )}
            </div>
          )}
          {activeEvent && (
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-50 border border-border" data-testid="sidebar-active-event">
              <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <CalendarDays size={14} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold truncate">{activeEvent.name}</p>
                <p className="text-[11px] text-slate-500">{activeEvent.event_date}</p>
              </div>
            </div>
          )}
          {!readOnly && (
            <>
              <Link
                to="/announcer"
                target="_blank"
                rel="noreferrer"
                data-testid="nav-announcer"
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-black text-white hover:bg-primary transition-colors text-sm font-medium"
              >
                <Tv2 size={16} />
                Open Announcer
              </Link>
              <Link
                to="/scan"
                target="_blank"
                rel="noreferrer"
                data-testid="nav-scan-app"
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-border hover:bg-slate-100 transition-colors text-sm font-medium"
              >
                <Smartphone size={16} />
                Open Scan App
              </Link>
            </>
          )}
          <button
            type="button"
            onClick={logout}
            data-testid={LOGOUT.button}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-border text-slate-600 hover:bg-slate-50 transition-colors text-sm font-medium"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="max-w-[1400px] mx-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
